import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/site/SiteNav";
import { Footer } from "@/components/site/Sections";
import {
  Tv,
  CheckCircle2,
  ArrowRight,
  ChevronRight,
  Sparkles,
  BarChart3,
  TrendingUp,
  Target,
  Users,
  Award,
  Globe,
  Clock,
  ShieldCheck,
  Check,
  Building2,
  FileCheck,
  Layers,
  Search,
  Zap,
  Play,
  Monitor,
  Smartphone,
  Maximize2,
  PieChart,
} from "lucide-react";

export const Route = createFileRoute("/services_/ott-advertising")({
  head: () => ({
    meta: [
      {
        title:
          "OTT & Connected TV Advertising Services — KMWAdvertising · Streaming Video Media Buying",
      },
      {
        name: "description",
        content:
          "OTT Advertising Services That Connect Your Brand with Premium Streaming Audiences. Connected TV (CTV), Live Sports & Programmatic OTT Media Buying by KMWA LLP.",
      },
      {
        property: "og:title",
        content: "OTT Advertising Services — KMWAdvertising",
      },
      {
        property: "og:description",
        content:
          "Reach High-Intent Audiences Through Connected TV & Streaming Platforms with targeted pre-roll, mid-roll, and CTV inventory.",
      },
    ],
  }),
  component: OTTAdvertisingServicePage,
});

export function OTTAdvertisingServicePage() {
  const whyOTTMatters = [
    {
      title: "Premium Viewing Environment",
      desc: "Your advertisements appear alongside professionally produced content including movies, TV series, live sports, and original entertainment, building strong brand authority.",
      icon: Tv,
    },
    {
      title: "Advanced Audience Targeting",
      desc: "Target specific viewer segments based on demographics, interest clusters, geographic locations, household characteristics, and device types.",
      icon: Target,
    },
    {
      title: "Higher Viewer Engagement",
      desc: "Streaming audiences are focused and attentive, driving higher video completion rates (VTR), message retention, and brand recall.",
      icon: Play,
    },
    {
      title: "Cross-Device Reach",
      desc: "Reach viewers seamlessly across Smart TVs, Connected TVs, smartphones, tablets, and desktop devices with synchronized messaging.",
      icon: Monitor,
    },
    {
      title: "Measurable Performance",
      desc: "Track video completion rates, reach, frequency, impression delivery, and downstream conversions with granular reporting.",
      icon: BarChart3,
    },
  ];

  const ottPlanningPoints = [
    "Comprehensive audience research & profiling",
    "Market analysis & competitor impression share",
    "Streaming platform selection (Disney+ Hotstar, JioCinema, SonyLIV, Zee5, etc.)",
    "Hyper-local & regional geographic targeting",
    "Optimized media budget allocation",
    "Blockbuster & live sports campaign scheduling",
    "Frequency capping & deduplicated reach planning",
  ];

  const ottBuyingCapabilities = [
    "Direct publisher & premium video inventory",
    "Connected TV (CTV) non-skippable inventory",
    "Programmatic OTT media buying via DSPs",
    "Regional language OTT campaign buying",
    "National marquee streaming placements",
    "Festive & seasonal streaming packages",
    "Live sports sponsorship & ad insertions (Cricket, ISL, Premier League)",
  ];

  const videoFormats = [
    {
      title: "Pre-Roll Video Ads",
      desc: "High-impact non-skippable or skippable commercials playing immediately before streaming content begins.",
      icon: Play,
    },
    {
      title: "Mid-Roll Video Ads",
      desc: "Engaging commercial breaks integrated naturally into long-form movies, TV series, and live broadcasts.",
      icon: Tv,
    },
    {
      title: "Post-Roll Ads",
      desc: "Closing commercials capturing audiences right after feature content completion.",
      icon: CheckCircle2,
    },
    {
      title: "Interactive Video Ads",
      desc: "Clickable overlays, product carousels, and QR code triggers allowing viewers to shop directly from TV screens.",
      icon: Sparkles,
    },
    {
      title: "Full-Screen CTV Ads",
      desc: "Uninterrupted, high-definition 15s/30s commercials designed specifically for living room Smart TV displays.",
      icon: Maximize2,
    },
  ];

  const targetingCapabilities = [
    "Demographic (Age, Gender, NCCS Classification)",
    "Geographic (City, Metro, State, Pincode level)",
    "Behavioural & Consumption Habits",
    "Interest & Content Genre Affinity",
    "Household Income & Device Value Targeting",
    "Smart TV vs Mobile Device Segmenting",
    "Contextual Genre & Live Sports Placement",
    "First-Party CRM Data Onboarding",
    "Lookalike High-Value Audiences",
    "Cross-Screen Retargeting Workflows",
  ];

  const industriesServed = [
    "FMCG & Consumer Goods",
    "Retail & E-commerce Brands",
    "Automobile Dealerships & OEMs",
    "Healthcare & Pharmaceuticals",
    "Education & EdTech Institutions",
    "Financial Services, Fintech & Banking",
    "Real Estate Developers & Luxury Housing",
    "Travel, Tourism & Hospitality",
    "Media, Entertainment & Gaming",
    "Technology & SaaS Enterprise Solutions",
  ];

  const kmwaStrengths = [
    "25+ years of strategic media planning heritage",
    "Certified OTT & digital advertising specialists",
    "Direct partnerships with top streaming platforms",
    "Programmatic DSP technology & CTV inventory access",
    "Data-driven audience segmentation & VTR optimization",
    "Creative video adaptation for vertical & 16:9 screens",
    "Transparent third-party verification & log reporting",
    "Brand-safe, premium content environment guarantees",
    "Full integration with TV, Print, OOH & Performance Media",
    "ROI-focused growth strategy",
  ];

  return (
    <div className="dark relative min-h-screen bg-background text-foreground flex flex-col w-full overflow-x-clip">
      <SiteNav />

      <main className="flex-1 w-full overflow-x-clip pt-24 md:pt-28">
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-14 sm:py-20 md:py-24 border-b border-white/10">
          <div className="absolute inset-0 grid-bg opacity-15" aria-hidden />
          <div className="glow-orb -left-32 top-10 h-[450px] w-[450px] bg-[var(--glow-purple)] opacity-25" />
          <div className="glow-orb right-[-100px] top-20 h-[400px] w-[400px] bg-[var(--glow-cyan)] opacity-25" />

          <div className="container-x relative z-10">
            {/* Breadcrumb */}
            <div className="flex flex-wrap items-center gap-2.5 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">
              <Link to="/" className="hover:text-white transition">
                Home
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <Link to="/services" className="hover:text-white transition">
                Services
              </Link>
              <ChevronRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-[var(--glow-purple)] font-semibold">OTT Advertising</span>
            </div>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-muted-foreground border border-white/10 mb-6">
                <Tv className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
                Digital Media Capabilities
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.12] tracking-tight text-white">
                OTT Advertising Services That Connect Your Brand with Premium Streaming Audiences
              </h1>

              <p className="mt-4 text-base font-mono text-white/80 uppercase tracking-widest">
                OTT & Connected TV (CTV) Media Planning & Buying
              </p>

              <p className="mt-4 text-lg sm:text-xl font-accent italic text-[var(--glow-purple)] font-medium border-l-2 border-[var(--glow-purple)] pl-4 py-1">
                Reach High-Intent Audiences Through Connected TV and OTT Advertising
              </p>

              <div className="mt-6 space-y-4 text-base sm:text-lg text-white/90 font-normal leading-relaxed">
                <p>
                  The way consumers watch content has changed dramatically. Millions of viewers now spend more time on Over-the-Top (OTT) platforms and Connected TV (CTV) than traditional television, consuming premium entertainment, live sports, movies and original content across smart TVs, mobile devices, tablets and desktops.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  OTT advertising enables brands to reach highly engaged audiences in a premium viewing environment where ads are delivered with advanced targeting capabilities and measurable campaign performance. Unlike traditional television, OTT campaigns combine the emotional storytelling of TV with the precision of digital marketing.
                </p>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  At <strong className="text-white">KMWAdvertising LLP</strong>, we help businesses leverage the power of OTT and Connected TV advertising through strategic media planning, audience targeting, creative video optimization, and programmatic media buying across leading platforms.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href="/contact-us" className="btn-hero !py-3.5 !px-7">
                  Request OTT Media Proposal
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link to="/services" className="btn-ghost !py-3.5 !px-7">
                  Explore All Services
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHY OTT ADVERTISING MATTERS */}
        <section className="py-14 sm:py-20 border-b border-white/10 bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Living Room & Cross-Device Reach
              </span>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                Why OTT Advertising Matters
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-3 leading-relaxed">
                OTT advertising bridges the gap between traditional television and digital marketing by combining premium video content with audience intelligence and real-time campaign optimization.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {whyOTTMatters.map((item, idx) => {
                const OIcon = item.icon;
                return (
                  <div
                    key={idx}
                    className="p-6 sm:p-8 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 flex flex-col justify-between group"
                  >
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)]">
                          PILLAR 0{idx + 1}
                        </span>
                        <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white">
                          <OIcon className="h-5 w-5 text-[var(--glow-purple)]" />
                        </div>
                      </div>
                      <h3 className="font-display text-xl font-bold text-white mb-2">
                        {item.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* MEDIA PLANNING, BUYING & CTV */}
        <section className="py-16 md:py-24 border-b border-white/10 relative">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                Strategic Media Execution
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Our OTT Advertising Services
              </h2>
            </div>

            {/* MEDIA PLANNING & BUYING GRID */}
            <div className="grid lg:grid-cols-2 gap-8 mb-12">
              {/* PLANNING */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-[var(--glow-purple)] bg-[var(--glow-purple)]/10 border border-[var(--glow-purple)]/30 mb-3">
                    <BarChart3 className="h-3.5 w-3.5" />
                    Strategic Audience Alignment
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">OTT Media Planning</h3>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Our specialists identify the most suitable streaming platforms, audience segments, and campaign formats to maximize visibility and video completion rates.
                </p>

                <div className="space-y-2 pt-2">
                  {ottPlanningPoints.map((pt, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{pt}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* BUYING */}
              <div className="p-8 sm:p-10 rounded-3xl glass-strong border border-white/15 space-y-6 bg-[#06060c]/90">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-mono text-sky-400 bg-sky-500/10 border border-sky-500/30 mb-3">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Programmatic & Direct Inventory
                  </div>
                  <h3 className="font-display text-2xl font-bold text-white">OTT Media Buying</h3>
                </div>

                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  We purchase premium inventory across leading OTT and Connected TV ecosystems using direct publisher partnerships and programmatic DSP technology.
                </p>

                <div className="space-y-2 pt-2">
                  {ottBuyingCapabilities.map((bc, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                      <span>{bc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* VIDEO FORMATS & CONNECTED TV */}
            <div className="mb-16">
              <div className="max-w-3xl mb-8">
                <h3 className="font-display text-2xl font-bold text-white">
                  OTT Video Formats & Connected TV (CTV)
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Connected TV advertising enables brands to deliver non-skippable, high-definition video advertisements directly on Smart TVs and streaming devices.
                </p>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {videoFormats.map((vf, idx) => {
                  const VIcon = vf.icon;
                  return (
                    <div key={idx} className="p-6 rounded-2xl glass-strong border border-white/10 hover:border-white/20 transition duration-300 space-y-3">
                      <div className="h-10 w-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[var(--glow-purple)]">
                        <VIcon className="h-5 w-5" />
                      </div>
                      <h4 className="font-display text-lg font-bold text-white">{vf.title}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">{vf.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* TARGETING & INDUSTRIES */}
            <div className="grid lg:grid-cols-12 gap-8 items-start">
              {/* Targeting */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Target className="h-5 w-5 text-[var(--glow-purple)]" />
                  Audience Targeting Capabilities
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Successful OTT campaigns depend on precise audience segmentation. We build custom targeting vectors to reduce wasted impressions:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {targetingCapabilities.map((tc, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
                      <span>{tc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Industries */}
              <div className="lg:col-span-6 p-8 rounded-3xl glass-strong border border-white/10 bg-[#06060c]/90 space-y-4">
                <h4 className="font-display text-xl font-bold text-white flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-sky-400" />
                  Industries We Serve
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  Our OTT advertising solutions support businesses across multiple sectors with industry-specific viewing behaviour strategies:
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  {industriesServed.map((ind, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/90">
                      <Check className="h-3.5 w-3.5 text-sky-400 shrink-0" />
                      <span>{ind}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE KMWA LLP */}
        <section className="py-16 md:py-24 border-b border-white/10 relative bg-white/[0.01]">
          <div className="container-x">
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-mono uppercase tracking-widest text-[var(--glow-purple)] block mb-2">
                25+ Years Media Excellence
              </span>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">
                Why Choose KMWAdvertising LLP?
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mt-2">
                Kris Mediaworks Advertising LLP combines traditional TV media expertise with modern digital streaming capabilities to deliver high-performance OTT campaigns.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {kmwaStrengths.map((str, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
                  <span className="font-mono text-xs text-[var(--glow-purple)] font-bold">0{i + 1}</span>
                  <p className="text-xs text-white/90 leading-snug font-medium">{str}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CLOSING CTA BANNER */}
        <section className="py-20 md:py-28 relative overflow-hidden">
          <div className="glow-orb left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] bg-[var(--glow-purple)] opacity-20" />

          <div className="container-x relative z-10 text-center max-w-4xl">
            <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-muted-foreground border border-white/10 mb-6">
              <Tv className="h-3.5 w-3.5 text-[var(--glow-purple)]" />
              OTT & CTV Advisory
            </div>

            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Ready to Reach Premium Streaming Audiences?
            </h2>

            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Contact our digital media experts today for a customised OTT advertising strategy and discover how Connected TV and streaming platforms can elevate your brand.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="/contact-us" className="btn-hero w-full sm:w-auto">
                Request OTT Strategy Proposal
                <ArrowRight className="h-4 w-4" />
              </a>
              <Link to="/services" className="btn-ghost w-full sm:w-auto">
                Back to All Services
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
